/**
 * KYC Data Mapper - Utility functions to map KYC verification data to form formats
 *
 * This module provides functions to transform KYC verification data from the API
 * into the format expected by the ReviewCardInfoDialog form.
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { Country } from '@workspace/ui/types';
import { transliterateCyrillic } from '@/lib/latin-validation';

// Country object from KYC API (can be either a string ISO code or full object)
export interface KYCCountryObject {
  id: number;
  name: string;
  dial_code: string;
  iso_code: string;
  iso_alpha_3_code?: string;
  continent?: string;
  currency_id?: number;
  national_id_required?: boolean;
  has_permanent_contract?: boolean;
  has_fixed_contract?: boolean;
  employable?: boolean;
}

// Country field can be a string (ISO code) or a full country object
export type KYCCountryField = string | KYCCountryObject | null | undefined;

// KYC API data structure (based on the provided schema)
export interface KYCPersonalDetails {
  tin?: string;
  country: KYCCountryField;
  last_name: string;
  first_name: string;
  last_name_en?: string;
  first_name_en?: string;
  dob: string; // Format: "2006-03-09"
  gender: 'M' | 'F' | string;
  country_of_birth: KYCCountryField;
  phone: string; // Format: "+306978530759"
  address:
    | {
        street: string;
        city?: string;
        post_code: string;
        region?: string;
        state?: string;
        state_en?: string;
      }
    | null
    | undefined;
}

export interface KYCVerificationData {
  id: number;
  user: {
    id: number;
    name: string;
    avatar: string;
    email: string;
    joined: string;
  };
  type: string;
  status: string;
  sumsub: {
    status: string;
    level: string;
    personal_details: KYCPersonalDetails;
    // ... other fields not needed for mapping
  };
}

// ReviewCardInfo form data structure
export interface ReviewCardFormData {
  firstName: string;
  lastName: string;
  title: string;
  gender: string;
  dateOfBirth: Date;
  phoneNumber: {
    id: string;
    prefix: string;
    number: string;
    iso_code: string;
    image: {
      path: string;
      alt: string;
    };
  };
  countryOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

/**
 * Maps gender from KYC format to form format
 * @param kycGender - 'M' | 'F' | other
 * @returns 'male' | 'female' | 'other'
 */
const mapGender = (kycGender: string): string => {
  console.log('🔄 mapGender: Input:', kycGender, 'Type:', typeof kycGender);
  const result = (() => {
    switch (kycGender?.toUpperCase()) {
      case 'M':
      case 'MALE':
        return 'male';
      case 'F':
      case 'FEMALE':
        return 'female';
      default:
        console.log('🔄 mapGender: Unknown gender value, defaulting to "other"');
        return 'other';
    }
  })();
  console.log('🔄 mapGender: Output:', result);
  return result;
};

/**
 * Maps title based on gender (since KYC doesn't provide title)
 * @param gender - The mapped gender
 * @returns Default title based on gender
 */
const mapTitle = (gender: string): string => {
  console.log('🔄 mapTitle: Input gender:', gender);
  const result = (() => {
    switch (gender) {
      case 'male':
        return 'Mr';
      case 'female':
        return 'Mrs';
      default:
        console.log('🔄 mapTitle: Unknown gender, defaulting to "Mr"');
        return 'Mr';
    }
  })();
  console.log('🔄 mapTitle: Output:', result);
  return result;
};

/**
 * Parses phone number from "+306978530759" format to prefix and number
 * @param phone - Full phone number with country code
 * @param countries - List of countries to find matching dial code
 * @returns Object with prefix, number, and country info
 */
const parsePhoneNumber = (
  phone: string,
  countries: Country[]
): {
  id: string;
  prefix: string;
  number: string;
  iso_code: string;
  image: { path: string; alt: string };
} => {
  console.log('📞 parsePhoneNumber: Input phone:', phone, 'Type:', typeof phone);
  console.log('📞 parsePhoneNumber: Countries available:', countries?.length);

  // Remove any non-digit characters except the leading +
  const cleanPhone = phone?.replace(/[^\d+]/g, '') || '';
  console.log('📞 parsePhoneNumber: Cleaned phone:', cleanPhone);

  // Find the longest matching dial code
  let matchedCountry: Country | null = null;
  let matchedPrefix = '';

  for (const country of countries) {
    const dialCode = country.dial_code.replace(/[^\d+]/g, '');
    if (cleanPhone.startsWith(dialCode) && dialCode.length > matchedPrefix.length) {
      matchedCountry = country;
      matchedPrefix = dialCode;
    }
  }

  console.log('📞 parsePhoneNumber: Matched country:', matchedCountry?.name || 'none');
  console.log('📞 parsePhoneNumber: Matched prefix:', matchedPrefix);

  // Extract the number part (everything after the dial code)
  const number = cleanPhone.substring(matchedPrefix.length);
  console.log('📞 parsePhoneNumber: Extracted number:', number);

  // If no country found, try to extract a reasonable prefix
  if (!matchedCountry) {
    console.log('📞 parsePhoneNumber: No country match, using fallback');
    // Default fallback - try to extract first 2-4 digits as prefix
    const prefixMatch = cleanPhone.match(/^(\+\d{1,4})/);
    matchedPrefix = prefixMatch?.[1] ?? '+1';
    console.log('📞 parsePhoneNumber: Fallback prefix:', matchedPrefix);

    const defaultCountry =
      countries.find((c) => c.dial_code === matchedPrefix) ||
      (countries.length > 0 ? countries[0] : null);

    if (!defaultCountry) {
      console.error('📞 parsePhoneNumber: No countries available!');
      throw new Error('No countries available for phone number parsing');
    }

    const result = {
      id: defaultCountry.id.toString(),
      prefix: matchedPrefix,
      number: cleanPhone.substring(matchedPrefix.length),
      iso_code: defaultCountry.iso_code,
      image: {
        path: `https://i.nativeteams.com/assets/app/flags_code/${defaultCountry.iso_code}.svg`,
        alt: defaultCountry.name,
      },
    };
    console.log('📞 parsePhoneNumber: Fallback result:', result);
    return result;
  }

  const result = {
    id: matchedCountry.id.toString(),
    prefix: matchedPrefix,
    number,
    iso_code: matchedCountry.iso_code,
    image: {
      path: `https://i.nativeteams.com/assets/app/flags_code/${matchedCountry.iso_code}.svg`,
      alt: matchedCountry.name,
    },
  };
  console.log('📞 parsePhoneNumber: Final result:', result);
  return result;
};

/**
 * Mapping of 3-letter ISO codes to 2-letter ISO codes for common countries
 * This handles the mismatch between KYC data (3-letter) and countries API (2-letter)
 */
const ISO3_TO_ISO2_MAP: Record<string, string> = {
  GRC: 'GR', // Greece
  USA: 'US', // United States
  GBR: 'GB', // United Kingdom
  FRA: 'FR', // France
  DEU: 'DE', // Germany
  ITA: 'IT', // Italy
  ESP: 'ES', // Spain
  NLD: 'NL', // Netherlands
  BEL: 'BE', // Belgium
  AUT: 'AT', // Austria
  CHE: 'CH', // Switzerland
  PRT: 'PT', // Portugal
  POL: 'PL', // Poland
  CZE: 'CZ', // Czech Republic
  HUN: 'HU', // Hungary
  SVK: 'SK', // Slovakia
  SVN: 'SI', // Slovenia
  HRV: 'HR', // Croatia
  BGR: 'BG', // Bulgaria
  ROU: 'RO', // Romania
  LTU: 'LT', // Lithuania
  LVA: 'LV', // Latvia
  EST: 'EE', // Estonia
  FIN: 'FI', // Finland
  SWE: 'SE', // Sweden
  DNK: 'DK', // Denmark
  NOR: 'NO', // Norway
  ISL: 'IS', // Iceland
  IRL: 'IE', // Ireland
  LUX: 'LU', // Luxembourg
  MLT: 'MT', // Malta
  CYP: 'CY', // Cyprus
  CAN: 'CA', // Canada
  MEX: 'MX', // Mexico
  BRA: 'BR', // Brazil
  ARG: 'AR', // Argentina
  CHL: 'CL', // Chile
  COL: 'CO', // Colombia
  PER: 'PE', // Peru
  VEN: 'VE', // Venezuela
  URY: 'UY', // Uruguay
  ECU: 'EC', // Ecuador
  BOL: 'BO', // Bolivia
  PRY: 'PY', // Paraguay
  JPN: 'JP', // Japan
  KOR: 'KR', // South Korea
  CHN: 'CN', // China
  IND: 'IN', // India
  IDN: 'ID', // Indonesia
  THA: 'TH', // Thailand
  VNM: 'VN', // Vietnam
  PHL: 'PH', // Philippines
  MYS: 'MY', // Malaysia
  SGP: 'SG', // Singapore
  AUS: 'AU', // Australia
  NZL: 'NZ', // New Zealand
  ZAF: 'ZA', // South Africa
  EGY: 'EG', // Egypt
  MAR: 'MA', // Morocco
  TUR: 'TR', // Turkey
  RUS: 'RU', // Russia
  UKR: 'UA', // Ukraine
  ISR: 'IL', // Israel
  ARE: 'AE', // United Arab Emirates
  SAU: 'SA', // Saudi Arabia
};

/**
 * Extracts ISO code from a country field (handles both string and object formats)
 * @param countryField - Can be a string ISO code or a country object with iso_code property
 * @returns The ISO code as a string, or null if not found
 */
const extractIsoCode = (countryField: KYCCountryField): string | null => {
  console.log('🌍 extractIsoCode: Input:', countryField, 'Type:', typeof countryField);

  if (!countryField) {
    console.log('🌍 extractIsoCode: No country field provided');
    return null;
  }

  // If it's a string, return it directly
  if (typeof countryField === 'string') {
    console.log('🌍 extractIsoCode: Country is a string:', countryField);
    return countryField;
  }

  // If it's an object with iso_code property, extract it
  if (typeof countryField === 'object' && 'iso_code' in countryField) {
    console.log(
      '🌍 extractIsoCode: Country is an object, extracting iso_code:',
      countryField.iso_code
    );
    return countryField.iso_code;
  }

  console.warn('🌍 extractIsoCode: Unknown country format:', countryField);
  return null;
};

/**
 * Finds country by ISO code (handles both 2-letter and 3-letter codes)
 * Also handles country objects from KYC API
 * @param countryField - Country from KYC - can be ISO code string or country object
 * @param countries - List of countries
 * @returns Country ISO code in lowercase for form
 */
const findCountryByIsoCode = (countryField: KYCCountryField, countries: Country[]): string => {
  console.log(
    '🌍 findCountryByIsoCode: Input countryField:',
    countryField,
    'Type:',
    typeof countryField
  );
  console.log('🌍 findCountryByIsoCode: Countries available:', countries?.length);

  // Extract ISO code from the field (handles both string and object)
  const isoCode = extractIsoCode(countryField);
  console.log('🌍 findCountryByIsoCode: Extracted ISO code:', isoCode);

  // Handle null, undefined, or non-string values
  if (!isoCode) {
    console.warn(`🌍 findCountryByIsoCode: Could not extract ISO code from:`, countryField);
    return '';
  }

  const upperIsoCode = isoCode.toUpperCase();
  console.log('🌍 findCountryByIsoCode: Uppercase ISO code:', upperIsoCode);

  // First try direct match (in case it's already 2-letter)
  let country = countries.find((c) => c.iso_code.toUpperCase() === upperIsoCode);
  console.log('🌍 findCountryByIsoCode: Direct match result:', country?.iso_code || 'not found');

  // If not found and it's 3-letter, try to convert to 2-letter
  if (!country && upperIsoCode.length === 3) {
    const iso2Code = ISO3_TO_ISO2_MAP[upperIsoCode];
    console.log(
      '🌍 findCountryByIsoCode: 3-letter code, looking up in ISO3_TO_ISO2_MAP:',
      upperIsoCode,
      '->',
      iso2Code
    );
    if (iso2Code) {
      country = countries.find((c) => c.iso_code.toUpperCase() === iso2Code);
      console.log(
        '🌍 findCountryByIsoCode: ISO2 conversion match result:',
        country?.iso_code || 'not found'
      );
    } else {
      console.warn(
        '🌍 findCountryByIsoCode: ISO code not found in ISO3_TO_ISO2_MAP:',
        upperIsoCode
      );
    }
  }

  // If still not found, log warning
  if (!country) {
    console.warn(
      `🌍 findCountryByIsoCode: Country not found for ISO code: ${isoCode}. Available countries:`,
      countries.map((c) => c.iso_code).slice(0, 20), // Only show first 20 to avoid spam
      '...'
    );
  }

  const result = country ? country.iso_code.toLowerCase() : '';
  console.log('🌍 findCountryByIsoCode: Final result:', result);
  return result;
};

/**
 * Converts date string to Date object
 * @param dateString - Date in "YYYY-MM-DD" format
 * @returns Date object
 */
const parseDate = (dateString: string): Date => {
  const parts = dateString.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    throw new Error(`Invalid date format: ${dateString}`);
  }
  const [year, month, day] = parts;

  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid date components in: ${dateString}`);
  }

  return new Date(year, month - 1, day); // month is 0-indexed in JS
};

/**
 * Maps KYC verification data to ReviewCardInfo form data
 * @param kycData - KYC verification data from API
 * @param countries - List of countries for mapping
 * @returns Partial form data that can be used to prefill the form
 */
export const mapKYCDataToFormData = (
  kycData: KYCVerificationData,
  countries: Country[]
): Partial<ReviewCardFormData> => {
  console.log('🗺️ MAP KYC TO FORM: Starting mapping');
  console.log('🗺️ MAP KYC TO FORM: Input kycData:', JSON.stringify(kycData, null, 2));
  console.log('🗺️ MAP KYC TO FORM: Countries count:', countries?.length);

  // Check if sumsub data exists
  if (!kycData?.sumsub?.personal_details) {
    console.warn('🗺️ MAP KYC TO FORM: Missing sumsub.personal_details, returning empty form data');
    console.warn('🗺️ MAP KYC TO FORM: kycData.sumsub:', kycData?.sumsub);
    return {
      firstName: '',
      lastName: '',
      title: '',
      gender: '',
      phoneNumber: {
        id: '',
        prefix: '',
        number: '',
        iso_code: '',
        image: { path: '', alt: '' },
      },
      countryOfBirth: '',
      address: '',
      zipCode: '',
      city: '',
      state: '',
      country: '',
    };
  }

  const personalDetails = kycData.sumsub.personal_details;
  console.log('🗺️ MAP KYC TO FORM: personalDetails:', JSON.stringify(personalDetails, null, 2));

  // Map gender
  console.log('🗺️ MAP KYC TO FORM: Mapping gender from:', personalDetails.gender);
  const mappedGender = mapGender(personalDetails.gender);
  console.log('🗺️ MAP KYC TO FORM: Mapped gender to:', mappedGender);

  // Map title
  const mappedTitle = mapTitle(mappedGender);
  console.log('🗺️ MAP KYC TO FORM: Mapped title to:', mappedTitle);

  // Map date of birth. Leave it empty when Sumsub has no usable date.
  console.log('🗺️ MAP KYC TO FORM: Mapping DOB from:', personalDetails.dob);
  let mappedDateOfBirth: Date | undefined;
  try {
    mappedDateOfBirth = personalDetails.dob ? parseDate(personalDetails.dob) : undefined;
    console.log('🗺️ MAP KYC TO FORM: Mapped DOB to:', mappedDateOfBirth);
  } catch (error) {
    console.error('🗺️ MAP KYC TO FORM: Error parsing DOB:', error);
    mappedDateOfBirth = undefined;
  }

  // Map phone number
  console.log('🗺️ MAP KYC TO FORM: Mapping phone from:', personalDetails.phone);
  let mappedPhoneNumber: ReviewCardFormData['phoneNumber'];
  try {
    mappedPhoneNumber = parsePhoneNumber(personalDetails.phone, countries);
    console.log('🗺️ MAP KYC TO FORM: Mapped phone to:', mappedPhoneNumber);
  } catch (error) {
    console.error('🗺️ MAP KYC TO FORM: Error parsing phone:', error);
    mappedPhoneNumber = {
      id: '',
      prefix: '',
      number: '',
      iso_code: '',
      image: { path: '', alt: '' },
    };
  }

  // Map country of birth
  console.log(
    '🗺️ MAP KYC TO FORM: Mapping country_of_birth from:',
    personalDetails.country_of_birth
  );
  const mappedCountryOfBirth = findCountryByIsoCode(personalDetails.country_of_birth, countries);
  console.log('🗺️ MAP KYC TO FORM: Mapped countryOfBirth to:', mappedCountryOfBirth);

  // Map country
  console.log('🗺️ MAP KYC TO FORM: Mapping country from:', personalDetails.country);
  const mappedCountry = findCountryByIsoCode(personalDetails.country, countries);
  console.log('🗺️ MAP KYC TO FORM: Mapped country to:', mappedCountry);

  // Map address fields
  console.log('🗺️ MAP KYC TO FORM: Address data:', personalDetails.address);
  const mappedAddress = transliterateCyrillic(personalDetails.address?.street);
  const mappedZipCode = transliterateCyrillic(personalDetails.address?.post_code);
  const mappedCity = transliterateCyrillic(personalDetails.address?.city);
  const mappedState = transliterateCyrillic(
    personalDetails.address?.region ||
      personalDetails.address?.state ||
      personalDetails.address?.state_en
  );
  console.log('🗺️ MAP KYC TO FORM: Mapped address:', mappedAddress);
  console.log('🗺️ MAP KYC TO FORM: Mapped zipCode:', mappedZipCode);
  console.log('🗺️ MAP KYC TO FORM: Mapped city:', mappedCity);
  console.log('🗺️ MAP KYC TO FORM: Mapped state:', mappedState);

  // Map name fields
  console.log('🗺️ MAP KYC TO FORM: Name fields:', {
    first_name: personalDetails.first_name,
    first_name_en: personalDetails.first_name_en,
    last_name: personalDetails.last_name,
    last_name_en: personalDetails.last_name_en,
  });
  const mappedFirstName = transliterateCyrillic(
    personalDetails.first_name_en || personalDetails.first_name
  );
  const mappedLastName = transliterateCyrillic(
    personalDetails.last_name_en || personalDetails.last_name
  );
  console.log('🗺️ MAP KYC TO FORM: Using firstName:', mappedFirstName);
  console.log('🗺️ MAP KYC TO FORM: Using lastName:', mappedLastName);

  const formData: Partial<ReviewCardFormData> = {
    firstName: mappedFirstName,
    lastName: mappedLastName,
    title: mappedTitle,
    gender: mappedGender,
    ...(mappedDateOfBirth ? { dateOfBirth: mappedDateOfBirth } : {}),
    phoneNumber: mappedPhoneNumber,
    countryOfBirth: mappedCountryOfBirth,
    // For delivery address, prefill from KYC data
    address: mappedAddress,
    zipCode: mappedZipCode,
    city: mappedCity,
    state: mappedState,
    country: mappedCountry,
  };

  console.log('🗺️ MAP KYC TO FORM: Final formData:', JSON.stringify(formData, null, 2));
  console.log('🗺️ MAP KYC TO FORM: Final formData breakdown:');
  console.log('  - firstName:', formData.firstName);
  console.log('  - lastName:', formData.lastName);
  console.log('  - title:', formData.title);
  console.log('  - gender:', formData.gender);
  console.log('  - dateOfBirth:', formData.dateOfBirth);
  console.log('  - phoneNumber:', formData.phoneNumber);
  console.log('  - countryOfBirth:', formData.countryOfBirth);
  console.log('  - address:', formData.address);
  console.log('  - city:', formData.city);
  console.log('  - state:', formData.state);
  console.log('  - zipCode:', formData.zipCode);
  console.log('  - country:', formData.country);

  return formData;
};

/**
 * Gets the most recent completed KYC verification from the list
 * @param kycVerifications - Array of KYC verifications
 * @returns The most recent completed verification or null
 */
export const getLatestCompletedKYC = (
  kycVerifications: KYCVerificationData[]
): KYCVerificationData | null => {
  // Filter for completed/approved verifications and get the most recent
  const completedVerifications = kycVerifications.filter(
    (kyc) =>
      kyc.sumsub.status === 'completed' ||
      kyc.sumsub.status === 'approved' ||
      kyc.status === 'approved'
  );

  if (completedVerifications.length === 0) {
    return null;
  }

  // Sort by ID (assuming higher ID = more recent) and return the latest
  const sortedVerifications = completedVerifications.sort((a, b) => b.id - a.id);
  return sortedVerifications[0] || null;
};
